import { useMemo } from 'react';
import { radiusPx } from '../../../theme/tokens/radius';
import { shadows } from '../../../theme/tokens/shadows';
import { controlHeights } from '../../../theme/tokens/spacing';
import { interveneRadius } from '../styles/interveneLayoutTokens';
import { useMessageBubbleContext } from '../components/chat/messages/MessageBubbleContext';
import useInterveneTheme from './useInterveneTheme';

function buildInnerSurface(chat, inner, isMine) {
  const received = chat.message?.received || {};

  if (isMine) {
    return {
      bg: 'rgba(255, 255, 255, 0.96)',
      text: chat.textPrimary,
      meta: chat.textSecondary,
      border: 'rgba(255, 255, 255, 0.35)',
      iconBg: chat.primarySurface || chat.primaryMuted,
      iconColor: chat.primary,
    };
  }

  return {
    bg: inner.ctaBg || received.bg || chat.paper,
    text: received.text || chat.textPrimary,
    meta: received.meta || chat.textSecondary,
    border: inner.ctaBorder || chat.divider,
    iconBg: chat.primarySurface || inner.ctaBg || received.bg || chat.paper,
    iconColor: chat.primary,
  };
}

export function createMessageStyles(chat, typography, { isMine = false } = {}) {
  const inner = chat.message?.inner || {};
  const sent = chat.message?.sent || {};
  const received = chat.message?.received || {};
  const innerSurface = buildInnerSurface(chat, inner, isMine);
  const linkColor = isMine ? sent.link || sent.text : received.link || chat.primary;
  const footerColor = isMine ? sent.meta || 'rgba(255,255,255,0.78)' : received.meta || chat.textMuted;
  const ctaText = isMine ? '#ffffff' : inner.ctaText || chat.primary;
  const ctaDivider = isMine ? 'rgba(255, 255, 255, 0.28)' : inner.divider || chat.divider;
  const invertedOptionSurface = {
    bg: '#FFFFFF',
    text: received.text || '#1C1E21',
    meta: inner.label || received.meta || '#667781',
    border: 'rgba(255, 255, 255, 0.35)',
  };

  return {
    messageRichStackSx: {
      display: 'flex',
      flexDirection: 'column',
      gap: 0,
      mx: -0.25,
    },
    messageTypeLabelSx: {
      fontSize: typography.fontSizeXs || '11px',
      fontWeight: typography.fontWeightMedium || 500,
      letterSpacing: '0.05em',
      textTransform: 'uppercase',
      color: isMine ? 'rgba(255, 255, 255, 0.72)' : inner.label || chat.textMuted,
      lineHeight: 1.3,
      mb: 0.75,
    },
    messageTemplateHeaderTextSx: {
      fontSize: typography.fontSizeMd || '14px',
      fontWeight: typography.fontWeightSemibold || 600,
      lineHeight: 1.4286,
      color: isMine ? sent.text || '#FFFFFF' : received.text || '#1C1E21',
      mb: 0.375,
    },
    messageQuoteSx: {
      borderLeft: `3px solid ${isMine ? 'rgba(255,255,255,0.65)' : inner.quoteBorder || chat.primary}`,
      bgcolor: isMine ? 'rgba(255,255,255,0.14)' : inner.quoteBg || chat.message?.feed?.background,
      borderRadius: `${radiusPx.sm} ${radiusPx.md} ${radiusPx.md} ${radiusPx.sm}`,
      px: 1,
      py: 0.75,
      mb: 0.5,
      color: isMine ? sent.text || '#FFFFFF' : received.text || '#1C1E21',
    },
    messageEmphasisTextSx: {
      fontSize: typography.fontSizeMd || '14px',
      fontWeight: typography.fontWeightSemibold || 600,
      lineHeight: 1.4286,
      color: isMine ? sent.text || '#FFFFFF' : received.text || '#1C1E21',
    },
    messageSecondaryTextSx: {
      fontSize: typography.fontSizeXs || '12px',
      lineHeight: 1.35,
      color: isMine ? sent.meta || 'rgba(255,255,255,0.78)' : received.meta || chat.textMuted,
    },
    messageInnerCardSx: {
      border: `1px solid ${innerSurface.border}`,
      borderRadius: radiusPx.md,
      px: 1,
      py: 0.875,
      bgcolor: innerSurface.bg,
      color: innerSurface.text,
    },
    messageDocumentCardSx: {
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      border: `1px solid ${innerSurface.border}`,
      borderRadius: interveneRadius.bubble,
      px: 0.875,
      py: 0.875,
      bgcolor: innerSurface.bg,
      color: innerSurface.text,
      boxShadow: shadows.sm,
      transition: 'background-color 0.15s ease, box-shadow 0.15s ease',
      '&:hover': {
        bgcolor: isMine ? '#ffffff' : innerSurface.bg,
        boxShadow: shadows.md,
      },
    },
    messageDocumentIconSx: {
      width: 44,
      height: 44,
      minWidth: 44,
      borderRadius: radiusPx.md,
      bgcolor: innerSurface.iconBg,
      color: innerSurface.iconColor,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    },
    messageDocumentIconLabelSx: {
      fontSize: '10px',
      fontWeight: 700,
      letterSpacing: '0.04em',
      lineHeight: 1,
    },
    messageDocumentTitleSx: {
      fontSize: typography.fontSizeSm || '13px',
      fontWeight: typography.fontWeightSemibold || 600,
      lineHeight: 1.35,
      color: innerSurface.text,
    },
    messageDocumentMetaSx: {
      fontSize: typography.fontSizeXs || '12px',
      lineHeight: 1.35,
      color: innerSurface.meta,
      mt: 0.125,
    },
    messageMediaFrameSx: {
      overflow: 'hidden',
      borderRadius: interveneRadius.bubble,
      bgcolor: 'transparent',
      position: 'relative',
      width: '100%',
      maxWidth: { xs: 220, sm: 260 },
      mb: 0,
      boxShadow: shadows.sm,
    },
    messageMediaFrameBleedSx: {
      mx: 0,
      mt: 0,
      mb: 0,
      borderRadius: interveneRadius.bubble,
    },
    messageMediaFrameBleedCompactSx: {
      mx: 0,
      mt: 0,
      mb: 0,
      borderRadius: interveneRadius.bubble,
      maxWidth: { xs: 220, sm: 260 },
    },
    messageMediaImageSx: {
      width: '100%',
      height: 'auto',
      display: 'block',
      maxHeight: 200,
      minHeight: 96,
      objectFit: 'cover',
      bgcolor: 'action.hover',
      verticalAlign: 'middle',
      borderRadius: interveneRadius.bubble,
    },
    messageMediaVideoSx: {
      width: '100%',
      display: 'block',
      maxHeight: 220,
      minHeight: 120,
      bgcolor: '#000',
      objectFit: 'cover',
      borderRadius: interveneRadius.bubble,
    },
    messageMediaMetaOverlaySx: {
      position: 'absolute',
      left: 0,
      right: 0,
      bottom: 0,
      px: 0.75,
      py: 0.5,
      background: 'linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.55) 100%)',
      pointerEvents: 'none',
    },
    messageMediaMetaTextSx: {
      fontSize: '11px',
      lineHeight: 1,
      fontWeight: typography.fontWeightMedium || 500,
      color: '#ffffff',
      opacity: 0.95,
      maxWidth: 140,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    messageMediaOverlaySx: {
      position: 'absolute',
      inset: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      bgcolor: 'rgba(0,0,0,0.22)',
      pointerEvents: 'none',
    },
    messageActionListSx: {
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'stretch',
      gap: 0,
      mt: 0.75,
      mx: -0.25,
      borderTop: `1px solid ${ctaDivider}`,
    },
    messageActionButtonSx: {
      width: '100%',
      minHeight: controlHeights.sm,
      height: 'auto',
      px: 0.5,
      py: 0.875,
      borderRadius: 0,
      justifyContent: 'center',
      fontWeight: typography.fontWeightMedium || 500,
      fontSize: typography.fontSizeMd || '14px',
      lineHeight: 1.4286,
      textAlign: 'center',
      color: ctaText,
      border: 'none',
      borderTop: `1px solid ${ctaDivider}`,
      bgcolor: 'transparent',
      boxShadow: 'none',
      textTransform: 'none',
      '&:first-of-type': {
        borderTop: 'none',
      },
      '&:hover': {
        bgcolor: isMine ? 'rgba(255,255,255,0.08)' : chat.primarySurface,
      },
      '&:focus-visible': {
        outline: 'none',
        boxShadow: `inset 0 0 0 2px ${isMine ? 'rgba(255,255,255,0.35)' : chat.primaryMuted}`,
      },
      '&:disabled': {
        opacity: 0.65,
        cursor: 'default',
      },
    },
    messageActionButtonWrapSx: {
      height: 'auto',
      minHeight: controlHeights.md,
      lineHeight: 1.35,
      whiteSpace: 'normal',
      overflow: 'visible',
      textOverflow: 'clip',
    },
    messageBodyTextSx: {
      lineHeight: 1.4286,
      fontSize: typography.fontSizeMd || '14px',
      fontWeight: typography.fontWeightRegular || 400,
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-word',
      letterSpacing: '0.01em',
      color: isMine ? sent.text || '#FFFFFF' : received.text || '#1C1E21',
      '& a': {
        color: linkColor,
        fontWeight: typography.fontWeightMedium || 500,
        textDecoration: 'underline',
        textUnderlineOffset: '2px',
      },
      '& strong, & em, & s, & code': {
        color: 'inherit',
      },
      '&:last-child': {
        marginBottom: 0,
      },
    },
    messageCaptionSx: {
      display: 'block',
      mt: 0.375,
      px: 0.125,
      pb: 0.125,
      color: isMine ? sent.text || '#FFFFFF' : received.text || '#1C1E21',
      fontSize: typography.fontSizeMd || '14px',
      fontWeight: typography.fontWeightRegular || 400,
      whiteSpace: 'pre-wrap',
      lineHeight: 1.4286,
    },
    messageFooterSx: {
      display: 'block',
      mt: 0.5,
      color: footerColor,
      fontSize: typography.fontSizeXs || '12px',
      lineHeight: 1.35,
    },
    messageOptionCardSx: {
      border: `1px solid ${isMine ? invertedOptionSurface.border : innerSurface.border}`,
      borderRadius: radiusPx.md,
      px: 1.25,
      py: 0.875,
      bgcolor: isMine ? invertedOptionSurface.bg : innerSurface.bg,
      color: isMine ? invertedOptionSurface.text : innerSurface.text,
      boxShadow: isMine ? shadows.sm : 'none',
      textAlign: isMine ? 'center' : 'left',
    },
    messageOptionTitleSx: {
      fontSize: typography.fontSizeMd || '14px',
      fontWeight: typography.fontWeightMedium || 500,
      lineHeight: 1.4286,
      color: isMine ? 'inherit' : received.text || '#1C1E21',
    },
    messageOptionDescriptionSx: {
      fontSize: typography.fontSizeXs || '12px',
      lineHeight: 1.35,
      mt: 0.25,
      color: isMine ? invertedOptionSurface.meta : received.meta || chat.textMuted,
      ...(isMine
        ? {
            '&&': {
              color: invertedOptionSurface.meta,
            },
          }
        : null),
    },
    messageMediaSx: {
      maxWidth: { xs: 220, sm: 260 },
      width: '100%',
      maxHeight: 200,
      borderRadius: radiusPx.md,
      objectFit: 'cover',
      display: 'block',
    },
    messageActionButtonPausedSx: {
      opacity: 0.65,
      cursor: 'not-allowed',
    },
    automationPausedHintSx: {
      display: 'block',
      px: 0.5,
      pt: 0.5,
      fontSize: typography.fontSizeXs || '11px',
      lineHeight: 1.35,
      fontStyle: 'italic',
      color: isMine ? 'rgba(255,255,255,0.78)' : chat.textMuted,
    },
  };
}

export default function useMessageStyles() {
  const { chat, typography } = useInterveneTheme();
  const { isMine } = useMessageBubbleContext();

  return useMemo(
    () => createMessageStyles(chat, typography, { isMine }),
    [chat, isMine, typography]
  );
}
